import * as pdfjsLib from "pdfjs-dist";
import proj4 from "proj4";
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@5.4.54/build/pdf.worker.min.mjs`;

export const convertPdfPageToImage = async (
  file: File,
  pageNumber: number = 1,
  scale: number = 1.5
): Promise<{ imageUrl: string; width: number; height: number }> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(pageNumber);

  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d")!;

  canvas.height = viewport.height;
  canvas.width = viewport.width;

  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";

  const renderContext = {
    canvasContext: context,
    viewport: viewport,
  };

  await page.render(renderContext).promise;
  return {
    imageUrl: canvas.toDataURL("image/png"),
    width: viewport.width / scale,
    height: viewport.height / scale,
  };
};

export const rotatePoint = (
  point: [number, number],
  center: [number, number],
  angle: number
): [number, number] => {
  const cos = Math.cos((angle * Math.PI) / 180);
  const sin = Math.sin((angle * Math.PI) / 180);
  const dx = point[0] - center[0];
  const dy = point[1] - center[1];
  return [center[0] + dx * cos - dy * sin, center[1] + dx * sin + dy * cos];
};

export const calculateRotatedCoordinates = (
  sizeScale: number,
  width: number,
  height: number,
  center: [number, number],
  rotation: number
): [[number, number], [number, number], [number, number], [number, number]] => {
  const centerWebMercator = proj4("EPSG:4326", "EPSG:3857", [
    center[0],
    center[1],
  ]);
  const pixelsToMeters = 1;
  const baseWidthMeters = width * pixelsToMeters;
  const baseHeightMeters = height * pixelsToMeters;
  const currentWidthMeters = baseWidthMeters * sizeScale;
  const currentHeightMeters = baseHeightMeters * sizeScale;

  // Calculate unrotated corners in Web Mercator
  const halfWidth = currentWidthMeters / 2;
  const halfHeight = currentHeightMeters / 2;

  const unrotatedCorners = [
    [centerWebMercator[0] - halfWidth, centerWebMercator[1] + halfHeight], // top-left
    [centerWebMercator[0] + halfWidth, centerWebMercator[1] + halfHeight], // top-right
    [centerWebMercator[0] + halfWidth, centerWebMercator[1] - halfHeight], // bottom-right
    [centerWebMercator[0] - halfWidth, centerWebMercator[1] - halfHeight], // bottom-left
  ];

  // Rotate all corners around center
  const rotatedCorners = unrotatedCorners.map(([x, y]) =>
    rotatePoint([x, y], centerWebMercator as [number, number], rotation)
  );

  // Convert back to lat/lng - use the actual rotated corners, not bounding box
  const result = rotatedCorners.map(([x, y]) =>
    proj4("EPSG:3857", "EPSG:4326", [x, y])
  ) as [[number, number], [number, number], [number, number], [number, number]];

  // const rotatedX = rotatedCorners.map(([x]) => x);
  // const rotatedY = rotatedCorners.map(([, y]) => y);

  // const boundingBox = {
  //   minX: Math.min(...rotatedX),
  //   maxX: Math.max(...rotatedX),
  //   minY: Math.min(...rotatedY),
  //   maxY: Math.max(...rotatedY),
  // };

  // // Create new corners from the bounding box
  // const boundingBoxCorners = [
  //   [boundingBox.minX, boundingBox.maxY], // top-left
  //   [boundingBox.maxX, boundingBox.maxY], // top-right
  //   [boundingBox.maxX, boundingBox.minY], // bottom-right
  //   [boundingBox.minX, boundingBox.minY], // bottom-left
  // ];

  // // Debug logging
  // console.log("Rotated Coordinates Calculation:", {
  //   rotation: rotation,
  //   unrotatedCorners: unrotatedCorners,
  //   rotatedCorners: rotatedCorners,
  //   finalCoordinates: result,
  //   boundingBoxCorners: boundingBoxCorners,
  // });

  return result;
};
